import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { propertiesService, Property } from '../services/properties.service';
import { categoriesService, Category } from '../services/categories.service';
import { expensesService, CreateExpenseData } from '../services/expenses.service';
import { documentsService, Document as DocType } from '../services/documents.service';
import { tw, monoFont } from '../utils/terminalTheme';

export const ExpenseForm: React.FC = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const urlPropertyId = searchParams.get('property');
  const [properties, setProperties] = useState<Property[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<DocType[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CreateExpenseData>({
    defaultValues: {
      property_id: propertyId ? parseInt(propertyId) : (urlPropertyId ? parseInt(urlPropertyId) : undefined),
      expense_date: new Date().toISOString().split('T')[0],
    },
  });

  useEffect(() => {
    loadData();

    const urlParams = new URLSearchParams(window.location.search);
    const urlDocumentId = urlParams.get('document_id');
    const urlPropertyId = urlParams.get('property_id');
    const urlAmount = urlParams.get('amount');
    const urlVendor = urlParams.get('vendor');
    const urlDescription = urlParams.get('description');
    const urlInvoice = urlParams.get('invoice');
    const urlDate = urlParams.get('date');
    const urlExpenseType = urlParams.get('expense_type');

    if (urlDocumentId || urlPropertyId || urlAmount || urlVendor) {
      if (urlPropertyId) {
        const propId = parseInt(urlPropertyId);
        setValue('property_id', propId);
        setSelectedPropertyId(propId);
      }
      if (urlDocumentId) setValue('document_id', parseInt(urlDocumentId));
      if (urlAmount) setValue('amount', parseFloat(urlAmount));
      if (urlVendor) setValue('vendor', urlVendor);

      let fullDescription = urlDescription || '';
      if (urlInvoice) {
        fullDescription = fullDescription ? `${fullDescription} (Invoice: ${urlInvoice})` : `Invoice: ${urlInvoice}`;
      }
      if (fullDescription) setValue('description', fullDescription);

      if (urlDate) {
        const dateObj = new Date(urlDate);
        if (!isNaN(dateObj.getTime())) {
          setValue('expense_date', dateObj.toISOString().split('T')[0]);
        }
      }

      if (urlExpenseType) {
        sessionStorage.setItem('pendingExpenseType', urlExpenseType);
      }
    } else {
      const pendingExpenseData = sessionStorage.getItem('pendingExpenseData');
      if (pendingExpenseData) {
        const data = JSON.parse(pendingExpenseData);
        sessionStorage.removeItem('pendingExpenseData');

        if (data.vendor) setValue('vendor', data.vendor);
        if (data.amount) setValue('amount', data.amount);
        if (data.date) {
          const dateObj = new Date(data.date);
          if (!isNaN(dateObj.getTime())) {
            setValue('expense_date', dateObj.toISOString().split('T')[0]);
          }
        }
        if (data.description) setValue('description', data.description);
        if (data.property_id) {
          setValue('property_id', data.property_id);
          setSelectedPropertyId(data.property_id);
        }
        if (data.document_id) setValue('document_id', data.document_id);
      }
    }

    const pendingDocId = sessionStorage.getItem('pendingExpenseDoc');
    if (pendingDocId) {
      sessionStorage.removeItem('pendingExpenseDoc');
    }
  }, [setValue]);

  useEffect(() => {
    if (selectedPropertyId) {
      loadPropertyDocuments(selectedPropertyId);
    }
  }, [selectedPropertyId]);

  const loadData = async () => {
    try {
      const propertiesData = await propertiesService.getProperties();
      setProperties(propertiesData);

      try {
        const categoriesData = await categoriesService.getExpenseCategories();
        setCategories(categoriesData);

        const pendingExpenseType = sessionStorage.getItem('pendingExpenseType');
        if (pendingExpenseType && categoriesData.length > 0) {
          sessionStorage.removeItem('pendingExpenseType');

          const typeMapping: { [key: string]: string } = {
            'utilities': 'Utilities',
            'maintenance': 'Professional Services',
            'insurance': 'Auto Insurance',
            'supplies': 'Office Supplies',
            'hoa': 'Professional Services',
            'property_management': 'Professional Services',
            'repairs': 'Professional Services',
            'cleaning': 'Professional Services',
            'landscaping': 'Professional Services',
            'legal': 'Legal',
            'accounting': 'Accounting',
            'marketing': 'Marketing & Advertising',
            'travel': 'Travel & Entertainment',
            'meals': 'Meals & Entertainment',
            'software': 'Software & Subscriptions',
            'office': 'Office Expenses',
            'vehicle': 'Vehicle Expenses',
            'gas': 'Gas & Fuel',
            'fuel': 'Gas & Fuel'
          };

          const mappedCategoryName = typeMapping[pendingExpenseType.toLowerCase()];
          if (mappedCategoryName) {
            const category = categoriesData.find(c => c.name === mappedCategoryName);
            if (category) {
              setValue('category_id', category.id);
            }
          }
        }
      } catch (categoryError) {
        console.error('Failed to load categories:', categoryError);
      }
    } catch (error) {
      toast.error('Failed to load properties');
      console.error('Load data error:', error);
    }
  };

  const loadPropertyDocuments = async (propertyId: number) => {
    try {
      const docs = await documentsService.getDocuments({ property_id: propertyId });
      const expenseDocuments = docs.filter(doc =>
        doc.document_type === 'receipt' ||
        doc.document_type === 'invoice' ||
        doc.document_type === 'other'
      );
      setDocuments(expenseDocuments);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    }
  };

  const handlePropertyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedPropertyId(value ? parseInt(value) : null);
  };

  const onSubmit = async (data: CreateExpenseData) => {
    setIsLoading(true);
    try {
      const formattedData = {
        ...data,
        amount: parseFloat(data.amount.toString()),
        expense_date: new Date(data.expense_date).toISOString(),
        property_id: data.property_id ? parseInt(data.property_id.toString()) : undefined,
        document_id: data.document_id ? parseInt(data.document_id.toString()) : undefined,
        category_id: data.category_id ? parseInt(data.category_id.toString()) : undefined,
      };

      await expensesService.createExpense(formattedData);
      toast.success('Expense created successfully');

      if (data.property_id) {
        navigate(`/properties/${data.property_id}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error('Failed to create expense');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={tw.page + ' max-w-2xl'}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>expenses/<span className="text-cyan-400">new</span>
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> create --expense
          </span>
        </h1>
      </div>

      <div className={tw.formWrap}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className={tw.label}>Property</label>
            <select
              {...register('property_id')}
              onChange={handlePropertyChange}
              className={tw.select}
            >
              <option value="">General/All Properties</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name} - {property.address}
                </option>
              ))}
            </select>
            {errors.property_id && (
              <p className="mt-1 text-xs text-red-400 font-mono">{errors.property_id.message}</p>
            )}
            <p className="mt-1 text-[10px] text-gray-600 font-mono">
              Leave as "General/All Properties" for business-wide expenses
            </p>
          </div>

          <div>
            <label className={tw.label}>Category</label>
            <select
              {...register('category_id')}
              className={tw.select}
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={tw.label}>Receipt/Invoice Document</label>
            <select
              {...register('document_id')}
              className={tw.select}
              disabled={!selectedPropertyId || documents.length === 0}
            >
              <option value="">No document attached</option>
              {documents.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.filename} ({doc.document_type}) - {new Date(doc.created_at).toLocaleDateString()}
                </option>
              ))}
            </select>
            {selectedPropertyId && documents.length === 0 && (
              <p className="mt-1 text-[10px] text-gray-600 font-mono">
                No receipts or invoices found for this property. Upload documents first.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={tw.label}>Amount *</label>
              <input
                type="number"
                step="0.01"
                {...register('amount', {
                  required: 'Amount is required',
                  min: { value: 0.01, message: 'Amount must be greater than 0' }
                })}
                className={tw.input}
                placeholder="0.00"
              />
              {errors.amount && (
                <p className="mt-1 text-xs text-red-400 font-mono">{errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className={tw.label}>Date *</label>
              <input
                type="date"
                {...register('expense_date', { required: 'Date is required' })}
                className={tw.input}
              />
              {errors.expense_date && (
                <p className="mt-1 text-xs text-red-400 font-mono">{errors.expense_date.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className={tw.label}>Vendor</label>
            <input
              type="text"
              {...register('vendor')}
              className={tw.input}
              placeholder="e.g., Home Depot, ABC Plumbing"
            />
          </div>

          <div>
            <label className={tw.label}>Description</label>
            <textarea
              {...register('description')}
              rows={3}
              className={tw.input}
              placeholder="Enter expense details..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={tw.btnSecondary}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={tw.btnPrimary + ' disabled:opacity-50 disabled:cursor-not-allowed'}
            >
              {isLoading ? 'Creating...' : 'Create Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
